import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { CarDetails, QualColId, SvvVehicle } from '../types'
import * as api from '../api/tecdocApi'
import type { ApiError } from '../types/api'

export const useVehicleStore = defineStore('vehicle', () => {
  // Search state
  const searchMode = ref<'regnr' | 'manual' | 'oem' | 'motorcode'>('regnr')
  const regNr = ref('')
  const oemNumber = ref('')

  // Vehicle identification
  const carId = ref<number | null>(null)
  const rmiCarId = ref<number | null>(null)
  const qualId = ref<number | null>(null)
  const hasGraphics = ref(false)
  const carDetails = ref<CarDetails | null>(null)

  // SVV details (Norwegian vehicle registry)
  const svvDetails = ref<any>(null)

  // Body type selection
  const bodyTypes = ref<QualColId[]>([])
  const showBodyTypeSelector = ref(false)

  // Loading/error
  const isSearching = ref(false)
  const searchError = ref<string | null>(null)

  // Fitment inputs — derived from carDetails so regnr flow (VIN) and motor flow
  // (motorCode only) both work without extra state.
  const selectedVin = computed<string | null>(() => {
    const v = carDetails.value?.vin
    return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null
  })
  const selectedMotorCode = computed<string | null>(() => {
    const m = carDetails.value?.motorCode
    return typeof m === 'string' && m.trim().length > 0 ? m.trim() : null
  })

  async function searchByRegNr(regNrInput: string) {
    isSearching.value = true
    searchError.value = null
    regNr.value = regNrInput

    try {
      const result = await api.searchByRegNr(regNrInput)
      carId.value = result.carId
      qualId.value = result.qualId
      hasGraphics.value = result.rmi
      carDetails.value = result.carDetails

      if (result.multipleQual && result.bodyTypes.length > 0) {
        bodyTypes.value = result.bodyTypes
        showBodyTypeSelector.value = true
      } else {
        showBodyTypeSelector.value = false
      }

      // Fetch SVV details in background (non-blocking)
      api.getSvvDetails(regNrInput).then(svv => {
        svvDetails.value = svv
      }).catch(() => {})
    } catch (err) {
      searchError.value = (err as ApiError).message
    } finally {
      isSearching.value = false
    }
  }

  async function selectMotor(motorCarId: number) {
    isSearching.value = true
    searchError.value = null

    try {
      // Use standard TecDoc carId lookup (not RMI) — resolves RMI internally
      const result = await api.getVehicleLookup(motorCarId)
      carId.value = result.carId
      qualId.value = result.qualId
      hasGraphics.value = result.rmi
      carDetails.value = result.carDetails

      if (result.multipleQual && result.bodyTypes.length > 0) {
        bodyTypes.value = result.bodyTypes
        showBodyTypeSelector.value = true
      } else {
        showBodyTypeSelector.value = false
      }
    } catch (err) {
      searchError.value = (err as ApiError).message
    } finally {
      isSearching.value = false
    }
  }

  // Restore a vehicle whose carId is an RMI id (graphics vehicles store the RMI id as carId).
  // Must use the RMI-specific lookup — RMI ids collide numerically with unrelated TecDoc carIds,
  // so the generic /vehicle/lookup endpoint would resolve a different car entirely.
  async function restoreByRmiId(rmiId: number) {
    isSearching.value = true
    searchError.value = null

    try {
      const result = await api.getVehicleByRmiId(rmiId)
      carId.value = result.carId
      qualId.value = result.qualId
      hasGraphics.value = result.rmi
      carDetails.value = result.carDetails

      if (result.multipleQual && result.bodyTypes.length > 0) {
        bodyTypes.value = result.bodyTypes
        showBodyTypeSelector.value = true
      } else {
        showBodyTypeSelector.value = false
      }
    } catch (err) {
      searchError.value = (err as ApiError).message
    } finally {
      isSearching.value = false
    }
  }

  function selectBodyType(selectedQualId: number) {
    qualId.value = selectedQualId
    showBodyTypeSelector.value = false
  }

  function reset() {
    carId.value = null
    rmiCarId.value = null
    qualId.value = null
    hasGraphics.value = false
    carDetails.value = null
    svvDetails.value = null
    bodyTypes.value = []
    showBodyTypeSelector.value = false
    searchError.value = null
  }

  return {
    searchMode,
    regNr,
    oemNumber,
    carId,
    rmiCarId,
    qualId,
    hasGraphics,
    carDetails,
    svvDetails,
    bodyTypes,
    showBodyTypeSelector,
    isSearching,
    searchError,
    searchByRegNr,
    selectMotor,
    restoreByRmiId,
    selectBodyType,
    reset,
    selectedVin,
    selectedMotorCode,
  }
})
